// src/pages/AdminLayout.jsx
import React, { useEffect, useContext } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, Newspaper, ShoppingBag, Star, Radio, LogOut, ExternalLink, Mail, Film, CalendarDays } from 'lucide-react';
import { AuthContext } from '../AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const NAV = [
  { to: '/admin',           label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/admin/users',     label: 'Users',      icon: Users },
  { to: '/admin/news',      label: 'News',       icon: Newspaper },
  { to: '/admin/store',     label: 'Store',      icon: ShoppingBag },
  { to: '/admin/spotlight', label: 'Spotlight',  icon: Star },
  { to: '/admin/live',      label: 'BGN Live',   icon: Radio },
  { to: '/admin/contact',    label: 'Messages',   icon: Mail },
  { to: '/admin/highlights', label: 'Highlights', icon: Film },
  { to: '/admin/events',     label: 'Events',     icon: CalendarDays },
];

const font = { mono: '"JetBrains Mono", monospace', condensed: '"Barlow Condensed", sans-serif' };

const AdminLayout = () => {
  const { authToken, isLoggedIn, adminUser, setAdminUser, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (!authToken || !isLoggedIn) { navigate('/'); return; }
    if (!adminUser) {
      fetch(`${BACKEND_URL}/api/admin/check`, {
        headers: { Authorization: `Bearer ${authToken}` }
      })
        .then(r => { if (!r.ok) throw new Error(); return r.json(); })
        .then(data => { if (!data.is_admin) throw new Error(); setAdminUser(data); })
        .catch(() => { logout(); navigate('/'); });
    }
  }, [authToken, isLoggedIn]);

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#000', color: '#fff' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;900&family=Barlow:wght@300;400&family=JetBrains+Mono:wght@400;700&display=swap');
        .anl { display:flex; align-items:center; gap:10px; padding:0.65rem 1.5rem; font-family:${font.mono}; font-size:0.65rem; letter-spacing:0.1em; color:#444; text-decoration:none; border-left:2px solid transparent; transition:all 0.15s; white-space:nowrap; }
        .anl:hover { color:#fff; background:#0d0d0d; }
        .anl.active { color:#fff; border-left-color:#fff; background:#0d0d0d; }
        .anl-btn { width:100%; background:none; border:none; cursor:pointer; text-align:left; }
        ::-webkit-scrollbar{width:3px} ::-webkit-scrollbar-track{background:#000} ::-webkit-scrollbar-thumb{background:#222}
      `}</style>

      {/* Sidebar */}
      <div style={{ width: 200, borderRight: '1px solid #111', display: 'flex', flexDirection: 'column', flexShrink: 0, position: 'sticky', top: 0, height: '100vh' }}>

        {/* Logo */}
        <div style={{ padding: '1.5rem', borderBottom: '1px solid #111' }}>
          <div style={{ fontFamily: font.condensed, fontWeight: 900, fontSize: '1.5rem', letterSpacing: '-0.02em' }}>BGN</div>
          <div style={{ fontFamily: font.mono, fontSize: '0.52rem', color: '#333', letterSpacing: '0.2em', marginTop: 2 }}>ADMIN PORTAL</div>
        </div>

        {/* Admin badge */}
        {adminUser && (
          <div style={{ padding: '0.9rem 1.5rem', borderBottom: '1px solid #111', display: 'flex', alignItems: 'center', gap: 8 }}>
            {adminUser.profile_image_url && (
              <img src={adminUser.profile_image_url} alt="" style={{ width: 26, height: 26, borderRadius: '50%', filter: 'grayscale(100%)', flexShrink: 0 }} />
            )}
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: font.mono, fontSize: '0.6rem', color: '#ccc', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>@{adminUser.login}</div>
              <div style={{ fontFamily: font.mono, fontSize: '0.5rem', color: '#ff4444', letterSpacing: '0.1em' }}>ADMIN</div>
            </div>
          </div>
        )}

        {/* Nav links */}
        <nav style={{ flex: 1, paddingTop: '0.5rem', overflowY: 'auto' }}>
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} className="anl">
              <Icon size={13} />{label.toUpperCase()}
            </NavLink>
          ))}
        </nav>

        {/* Footer actions */}
        <div style={{ borderTop: '1px solid #111', paddingBottom: '0.5rem' }}>
          <a href={process.env.REACT_APP_FRONTEND_URL || 'http://localhost:3000'} target="_blank" rel="noopener noreferrer" className="anl">
            <ExternalLink size={13} /> VIEW SITE
          </a>
          <button className="anl anl-btn" onClick={() => { logout(); navigate('/'); }} style={{ color: '#553333' }}
            onMouseEnter={e => e.currentTarget.style.color = '#ff6b6b'}
            onMouseLeave={e => e.currentTarget.style.color = '#553333'}
          >
            <LogOut size={13} /> LOGOUT
          </button>
        </div>
      </div>

      {/* Page content */}
      <div style={{ flex: 1, overflow: 'auto', minHeight: '100vh' }}>
        <Outlet />
      </div>
    </div>
  );
};

export default AdminLayout;